import { useCallback, useEffect, useState } from 'react'
import { AlertCircle, ExternalLink, Loader2, MonitorSmartphone, Plus, RefreshCw, Save, Trash2 } from 'lucide-react'
import { appSyncApi } from '../../../../services/appSyncApi'
import { applicationsApi } from '../../../../services/applicationsApi'
import { templatesApi } from '../../../../services/templatesApi'
import { openManagedDeviceWebUiWindow } from '../../../../utils/openDeviceWebUiWindow'
import { formatDateTime } from '../../../../utils/format'

const DEFAULT_SYNC = { appId: 'aeromais-server', command: 'sudo -n /usr/local/sbin/aeromais-sync', intervalSeconds: 300 }

/**
 * Tab "Tela + Sync" do ManagedDevice (GDF528):
 *  A) Tela — Application + Template webui que desenham a tela do device ("Abrir tela").
 *  B) Sincronização — a nuvem puxa por SSH os dados do app do device (somente leitura no device).
 *  C) Dados sincronizados — tabelas e contagens da cópia na nuvem.
 */
export default function AppScreenSection({ device }) {
  const deviceId = device?.id
  const tenantId = device?.tenantId

  const [applications, setApplications] = useState([])
  const [templates, setTemplates] = useState([])
  const [screen, setScreen] = useState({ applicationId: '', templateId: '' })
  const [syncs, setSyncs] = useState([])
  const [tables, setTables] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [savingScreen, setSavingScreen] = useState(false)
  const [running, setRunning] = useState({})
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState(DEFAULT_SYNC)

  const loadAll = useCallback(async () => {
    if (!deviceId || !tenantId) return
    setLoading(true)
    setError(null)
    try {
      const [apps, scr, sy, tb] = await Promise.all([
        applicationsApi.getByTenant(tenantId),
        appSyncApi.getScreen(deviceId),
        appSyncApi.list(deviceId),
        appSyncApi.tables(deviceId),
      ])
      setApplications(apps || [])
      setScreen({ applicationId: scr?.applicationId || '', templateId: scr?.templateId || '' })
      setSyncs(sy || [])
      setTables(tb || [])
    } catch (e) {
      setError(e.response?.data?.message || e.message)
    } finally {
      setLoading(false)
    }
  }, [deviceId, tenantId])

  useEffect(() => {
    loadAll()
  }, [loadAll])

  useEffect(() => {
    if (!screen.applicationId) {
      setTemplates([])
      return
    }
    templatesApi
      .getByApplication(screen.applicationId)
      .then((list) => setTemplates((list || []).filter((t) => (t.type || 'webui') === 'webui')))
      .catch(() => setTemplates([]))
  }, [screen.applicationId])

  const saveScreen = async () => {
    setSavingScreen(true)
    try {
      const saved = await appSyncApi.setScreen(deviceId, {
        applicationId: screen.applicationId || null,
        templateId: screen.templateId || null,
      })
      setScreen({ applicationId: saved.applicationId || '', templateId: saved.templateId || '' })
    } catch (e) {
      alert(e.response?.data?.message || e.message)
    } finally {
      setSavingScreen(false)
    }
  }

  const runNow = async (syncId) => {
    setRunning((s) => ({ ...s, [syncId]: true }))
    try {
      const r = await appSyncApi.run(syncId)
      if (!r?.success) alert(`Sincronização falhou: ${r?.error || 'sem detalhes'}`)
      await loadAll()
    } catch (e) {
      alert(e.response?.data?.message || e.message)
    } finally {
      setRunning((s) => ({ ...s, [syncId]: false }))
    }
  }

  const toggleEnabled = async (sync) => {
    try {
      await appSyncApi.update(sync.id, { enabled: !sync.enabled })
      await loadAll()
    } catch (e) {
      alert(e.response?.data?.message || e.message)
    }
  }

  const removeSync = async (sync) => {
    if (!window.confirm(`Parar de sincronizar "${sync.appId}"? Os dados já copiados continuam na nuvem.`)) return
    try {
      await appSyncApi.remove(sync.id)
      await loadAll()
    } catch (e) {
      alert(e.response?.data?.message || e.message)
    }
  }

  const createSync = async (e) => {
    e.preventDefault()
    try {
      await appSyncApi.create(deviceId, { ...form, intervalSeconds: Number(form.intervalSeconds) || 300, enabled: true })
      setShowForm(false)
      setForm(DEFAULT_SYNC)
      await loadAll()
    } catch (err) {
      alert(err.response?.data?.message || err.message)
    }
  }

  const screenConfigured = Boolean(screen.applicationId && screen.templateId)

  return (
    <div className="space-y-4">
      {error && (
        <div className="flex items-center gap-2 text-sm text-error-600 bg-error-50 border border-error-200 rounded-lg p-3">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      {/* A) Tela */}
      <section className="bg-theme-surface border border-theme-surface-border rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-theme-text flex items-center gap-2">
            <MonitorSmartphone className="w-4 h-4 text-theme-secondary" /> Tela do device
          </h3>
          <button
            type="button"
            disabled={!screenConfigured}
            onClick={() => openManagedDeviceWebUiWindow(deviceId)}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-theme-primary text-white disabled:opacity-40"
          >
            <ExternalLink className="w-3.5 h-3.5" /> Abrir tela
          </button>
        </div>
        <p className="text-xs text-theme-text-muted">
          Template webui (da Application) que mostra os dados sincronizados do app do device. Somente leitura.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2 items-end">
          <label className="text-xs text-theme-text-muted">
            Application
            <select
              className="mt-1 w-full border border-theme-border rounded-md px-2 py-1.5 text-sm text-theme-text bg-theme-surface"
              value={screen.applicationId}
              onChange={(e) => setScreen({ applicationId: e.target.value, templateId: '' })}
            >
              <option value="">— nenhuma —</option>
              {applications.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </label>
          <label className="text-xs text-theme-text-muted">
            Template
            <select
              className="mt-1 w-full border border-theme-border rounded-md px-2 py-1.5 text-sm text-theme-text bg-theme-surface"
              value={screen.templateId}
              disabled={!screen.applicationId}
              onChange={(e) => setScreen((s) => ({ ...s, templateId: e.target.value }))}
            >
              <option value="">— nenhum —</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={saveScreen}
            disabled={savingScreen || (screen.applicationId && !screen.templateId)}
            className="inline-flex items-center justify-center gap-1.5 text-xs px-3 py-2 rounded-md border border-theme-border text-theme-text disabled:opacity-40"
          >
            {savingScreen ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Salvar
          </button>
        </div>
      </section>

      {/* B) Sincronização */}
      <section className="bg-theme-surface border border-theme-surface-border rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-theme-text">Sincronização de dados do app</h3>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={loadAll}
              className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-theme-border text-theme-text"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Atualizar
            </button>
            <button
              type="button"
              onClick={() => setShowForm((v) => !v)}
              className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md bg-theme-primary text-white"
            >
              <Plus className="w-3.5 h-3.5" /> Adicionar
            </button>
          </div>
        </div>
        <p className="text-xs text-theme-text-muted">
          A nuvem roda o comando por SSH (mesmo acesso do backup) e guarda uma cópia com histórico de versões.
          Sincroniza ao reconectar e a cada intervalo enquanto o device está online. Nada é escrito no device.
        </p>

        {showForm && (
          <form onSubmit={createSync} className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto_auto] gap-2 items-end">
            <label className="text-xs text-theme-text-muted">
              App
              <input
                className="mt-1 w-full border border-theme-border rounded-md px-2 py-1.5 text-sm font-mono bg-theme-surface text-theme-text"
                value={form.appId}
                onChange={(e) => setForm((f) => ({ ...f, appId: e.target.value }))}
              />
            </label>
            <label className="text-xs text-theme-text-muted">
              Comando
              <input
                className="mt-1 w-full border border-theme-border rounded-md px-2 py-1.5 text-sm font-mono bg-theme-surface text-theme-text"
                value={form.command}
                onChange={(e) => setForm((f) => ({ ...f, command: e.target.value }))}
              />
            </label>
            <label className="text-xs text-theme-text-muted">
              Intervalo (s)
              <input
                type="number"
                min={60}
                max={86400}
                className="mt-1 w-28 border border-theme-border rounded-md px-2 py-1.5 text-sm bg-theme-surface text-theme-text"
                value={form.intervalSeconds}
                onChange={(e) => setForm((f) => ({ ...f, intervalSeconds: e.target.value }))}
              />
            </label>
            <button type="submit" className="text-xs px-3 py-2 rounded-md bg-theme-primary text-white">Criar</button>
          </form>
        )}

        {syncs.length === 0 ? (
          <p className="text-sm text-theme-text-muted">Nenhuma sincronização configurada.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-theme-text-muted text-left">
                <tr>
                  <th className="py-1 pr-3">App</th>
                  <th className="py-1 pr-3">Comando</th>
                  <th className="py-1 pr-3">Intervalo</th>
                  <th className="py-1 pr-3">Último sucesso</th>
                  <th className="py-1 pr-3">Situação</th>
                  <th className="py-1" />
                </tr>
              </thead>
              <tbody className="text-theme-text">
                {syncs.map((s) => (
                  <tr key={s.id} className="border-t border-theme-border align-top">
                    <td className="py-2 pr-3 font-mono">{s.appId}</td>
                    <td className="py-2 pr-3 font-mono break-all">{s.command}</td>
                    <td className="py-2 pr-3">{s.intervalSeconds}s</td>
                    <td className="py-2 pr-3">{s.lastSuccessAt ? formatDateTime(s.lastSuccessAt) : '—'}</td>
                    <td className="py-2 pr-3">
                      {!s.enabled ? (
                        <span className="text-gray-500">desligada</span>
                      ) : s.lastError ? (
                        <span className="text-error-600" title={s.lastError}>erro: {s.lastError.slice(0, 80)}</span>
                      ) : (
                        <span className="text-success-600">ok</span>
                      )}
                    </td>
                    <td className="py-2 whitespace-nowrap text-right space-x-1">
                      <button
                        type="button"
                        onClick={() => runNow(s.id)}
                        disabled={running[s.id]}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded border border-theme-border"
                        title="Sincronizar agora"
                      >
                        {running[s.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Agora
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleEnabled(s)}
                        className="px-2 py-1 rounded border border-theme-border"
                      >
                        {s.enabled ? 'Desligar' : 'Ligar'}
                      </button>
                      <button
                        type="button"
                        onClick={() => removeSync(s)}
                        className="px-2 py-1 rounded border border-theme-border text-error-600"
                        title="Remover configuração (mantém os dados)"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* C) Dados sincronizados */}
      <section className="bg-theme-surface border border-theme-surface-border rounded-lg p-4 space-y-2">
        <h3 className="text-sm font-semibold text-theme-text">Dados na nuvem</h3>
        {tables.length === 0 ? (
          <p className="text-sm text-theme-text-muted">Ainda não há dados sincronizados.</p>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-theme-text-muted text-left">
              <tr>
                <th className="py-1 pr-3">Tabela</th>
                <th className="py-1 pr-3">Linhas</th>
                <th className="py-1 pr-3">Alteradas no device</th>
                <th className="py-1 pr-3">Ausentes no device</th>
                <th className="py-1 pr-3">Registro mais recente</th>
                <th className="py-1">Visto por último</th>
              </tr>
            </thead>
            <tbody className="text-theme-text">
              {tables.map((t) => (
                <tr key={t.tabela} className="border-t border-theme-border">
                  <td className="py-1.5 pr-3 font-mono">{t.tabela}</td>
                  <td className="py-1.5 pr-3">{t.total}</td>
                  <td className="py-1.5 pr-3">{t.alteradas || '—'}</td>
                  <td className="py-1.5 pr-3">{t.ausentes || '—'}</td>
                  <td className="py-1.5 pr-3">{t.ultimoMomento ? formatDateTime(t.ultimoMomento) : '—'}</td>
                  <td className="py-1.5">{t.ultimaAtualizacao ? formatDateTime(t.ultimaAtualizacao) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  )
}
